import React, { useCallback, useEffect, useRef, useState } from 'react';
import { ScrollView, StyleSheet } from 'react-native';
import type WebView from 'react-native-webview';
import { theme, useNoticeBannersStackContext } from '@sweetgreen/garnish';

import { useLocalizationContext } from '@order/Localization';

import { CreditCardFormControlledContent } from './components/CreditCardForm.controlled';
import { defaultCreditCardForm } from './CreditCardForm.constants';
import type { FormProps } from './CreditCardForm.types';
import { useBraintreeMessages } from './hooks/Braintree.hooks';
import { useCreditCardTelemetry } from './hooks/CreditCardForm.hooks.telemetry';
import { FRAME_HEIGHT } from './hosted-fields/constants';
import { useBraintreeWebSource } from './hosted-fields/CreditCardForm.webView';
import {
  CreditCardFormHost,
  injectTokenizeRequest,
} from './hosted-fields/CreditCardFormHost';

export const CreditCardFormContent = ({
  vault,
  account,
  subtitle,
  title,
  mode,
  isSaving,
  isDeleting,
  onSave,
  onEdit,
  footerFullWidth,
  onDelete,
  excludedFields = [],
}: FormProps) => {
  const isEditing = mode === 'edit';

  // ────── Controlled Form State ───────────────────────────────────────
  const [form, setForm] = useState({
    nickname: account?.nickname ?? defaultCreditCardForm.nickname,
    default: account?.isDefault ?? defaultCreditCardForm.default,
  });

  // ────── Telemetry ───────────────────────────────────────────────────
  const {
    monitoredOnSave,
    monitoredOnEdit,
    monitoredOnFail,
    monitoredOnDelete,
  } = useCreditCardTelemetry({
    isEditing,
    onSave,
    onEdit,
    onDelete,
  });

  // ────── WebView Setup ───────────────────────────────────────────────
  const braintreeWebSource = useBraintreeWebSource(vault);
  const webViewRef = useRef<WebView>();
  const {
    nonce,
    isLoadingFields,
    isTokenizing,
    hasCardNumber,
    hasExpDate,
    hasCvv,
    hasPostalCode,
    handleBraintreeMessages,
  } = useBraintreeMessages({ isEditing, onError: monitoredOnFail });

  // ────── Submitting New Card ─────────────────────────────────────────
  const saveReference = useRef<typeof monitoredOnSave>();

  // eslint-disable-next-line functional/immutable-data
  saveReference.current = monitoredOnSave;

  useEffect(() => {
    if (nonce) saveReference.current?.(form, nonce);
  }, [nonce, form]);

  // ─────────────────────── Callbacks ──────────────────────────────────
  const { push: addNoticeBanner } = useNoticeBannersStackContext();
  const { t } = useLocalizationContext();
  const submitForm = useCallback(() => {
    (async () => {
      if (isLoadingFields) {
        addNoticeBanner({
          palette: 'caution',
          text: t('account.credit-card-form.loading.on-submit'),
        });

        return;
      }

      if (!hasCardNumber) {
        addNoticeBanner({
          palette: 'caution',
          text: t('account.credit-card-form.number.on-submit'),
        });

        return;
      }

      if (!hasExpDate) {
        addNoticeBanner({
          palette: 'caution',
          text: t('account.credit-card-form.expiration.on-submit'),
        });

        return;
      }

      if (!hasCvv) {
        addNoticeBanner({
          palette: 'caution',
          text: t('account.credit-card-form.cvv.on-submit'),
        });

        return;
      }

      if (!hasPostalCode) {
        addNoticeBanner({
          palette: 'caution',
          text: t('account.credit-card-form.zipCode.on-submit'),
        });

        return;
      }

      if (isEditing) {
        monitoredOnEdit(form);

        return;
      }

      injectTokenizeRequest(webViewRef);
    })();
  }, [
    form,
    isEditing,
    isLoadingFields,
    hasCardNumber,
    hasExpDate,
    hasCvv,
    hasPostalCode,
    t,
    addNoticeBanner,
    monitoredOnEdit,
  ]);

  return (
    <ScrollView
      testID="credit-card-form"
      style={footerFullWidth ? styles.flex : {}}
      contentContainerStyle={styles.content}
    >
      <CreditCardFormControlledContent
        account={account}
        subtitle={subtitle}
        isSaving={Boolean(isSaving) || isTokenizing}
        mode={mode}
        isDeleting={isDeleting}
        isLoadingFields={isLoadingFields}
        canSubmitForm={true}
        form={form}
        title={title}
        footerFullWidth={footerFullWidth}
        excludedFields={excludedFields}
        fadeDuration={0}
        onFormChange={setForm}
        onSubmitForm={submitForm}
        onDelete={monitoredOnDelete}
      >
        {isEditing ? null : (
          <CreditCardFormHost
            ref={webViewRef}
            isLoadingFields={isLoadingFields}
            source={braintreeWebSource}
            onMessage={handleBraintreeMessages}
            style={styles.webView}
            containerStyle={[
              styles.container,
              isLoadingFields && styles.loadingFields,
            ]}
          />
        )}
      </CreditCardFormControlledContent>
    </ScrollView>
  );
};

const styles = StyleSheet.create({
  flex: {
    display: 'flex',
  },
  container: {
    width: '100%',
    height: FRAME_HEIGHT,
    backgroundColor: theme.colors.OATMEAL,
    paddingHorizontal: theme.spacing.small,
  },
  // for web views to function properly on Android, their container must be at least 1x1 in size
  loadingFields: {
    flex: 0,
    width: 1,
    height: 1,
  },
  content: {
    backgroundColor: theme.colors.OATMEAL,
  },
  webView: {
    backgroundColor: theme.colors.OATMEAL,
  },
});
